import { RegionShared } from "@1games-ru/region-shared";
import clsx from "clsx";
import { MAX_RPM } from "@/core/constants/vehicleLimits";
import {
  EngineIcon,
  FuelIcon,
  KeyIcon,
  SeatbeltIcon,
  SpeedometerIcon,
  TemperatureIcon,
} from "@/shared/icons";
import IndicatorClassicScale from "@/domains/hud/components/speedometer/indicator/IndicatorClassicScale";
import IndicatorCounter from "@/domains/hud/components/speedometer/indicator/IndicatorCounter";

type Props = {
  className?: string;
} & typeof RegionShared.Cef.Requests.SetVehicleStateRequest;

export default function HudSpeedometerClassic({
  className,
  fuel,
  fuelCapacity,
  mileage,
  oilTemperature,
  rpm,
  speed,
  isEngine,
  isSeatBelt,
  isLocked,
}: Props) {
  const rmpPercent = Number(((rpm / MAX_RPM) * 100).toFixed(0));
  const fuelPercent = fuelCapacity === 0 ? 0 : Number(((fuel / fuelCapacity) * 100).toFixed(0));

  return (
    <div
      className={clsx(
        `relative aspect-square shrink-0 w-[262px] bg-[#130F14]/50 p-4
         flex flex-col items-center justify-between rounded-full overflow-hidden`,
        className,
      )}
    >
      <div className="absolute left-4 top-[50%] flex flex-col -translate-y-[50%]">
        <div className="relative">
          <SpeedometerIcon size={20} className="absolute w-6 text-white/50 -top-6 left-6" />
          <IndicatorClassicScale fillPercent={rmpPercent} />
        </div>
      </div>
      <div className="absolute right-4 top-[50%] flex flex-col -translate-y-[50%]">
        <div className="relative">
          <FuelIcon size={20} className="absolute text-white/50 -top-6 right-6" />
          <IndicatorClassicScale
            fillPercent={fuelPercent}
            style={{ transform: "rotateY(180deg)" }}
          />
        </div>
      </div>

      <div className="w-full grid grid-cols-2 items-center gap-1 -translate-y-1">
        <TemperatureIcon size={20} className="text-white/50 justify-self-end" />
        <p className="text-white/50 text-12 font-medium">{oilTemperature}&#176;</p>
      </div>
      <div className="flex flex-col items-center w-full mr-1.5">
        <IndicatorCounter
          value={mileage}
          cols={6}
          unit="km"
          ceilHeight={20}
          className="gap-0.5"
          classNameCeil="w-4 bg-black/50 rounded"
          classNameStep="font-bold text-12 text-white tabular-nums"
        />
        <IndicatorCounter
          value={speed}
          cols={3}
          ceilHeight={60}
          className="gap-0.5 py-3"
          classNameCeil="w-9"
          classNameStep="font-bold text-[60px] text-white leading-[0.75] font-bold tabular-nums"
        />
        <p className="text-14 text-white/50 leading-[0.75] font-medium">km/h</p>
      </div>
      <div className="flex items-end gap-7">
        <div className="flex flex-col items-end gap-1 -translate-y-3">
          <span
            className={clsx(
              "font-bold text-12 leading-[0.75]",
              isEngine ? "text-white" : "text-white/50",
            )}
          >
            B
          </span>
          <EngineIcon size={20} className={isEngine ? "text-white" : "text-white/50"} />
        </div>
        <div className="flex flex-col items-center gap-1">
          <span
            className={clsx(
              "font-bold text-12 leading-[0.75]",
              isSeatBelt ? "text-white" : "text-white/50",
            )}
          >
            J
          </span>
          <SeatbeltIcon size={20} className={isSeatBelt ? "text-white" : "text-white/50"} />
        </div>
        <div className="flex flex-col items-start gap-1 -translate-y-3">
          <span
            className={clsx(
              "font-bold text-12 leading-[0.75]",
              isLocked ? "text-white" : "text-white/50",
            )}
          >
            L
          </span>
          <KeyIcon size={20} className={isLocked ? "text-white" : "text-white/50"} />
        </div>
      </div>
    </div>
  );
}
